import { MeetingRescheduleReasonSection } from "./MeetingRescheduleReasonSection";
import { TimesHistorySection } from "./TimesHistorySection";
import {
  Box,
  Card,
  Chip,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { ArrowRightIcon, Clock, NotepadIcon, } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment-timezone";
import { useEffect, useReducer, useRef, useState } from "react";
import {
  useLocation,
  useParams,
} from "react-router";
import { useNavigate } from "react-router-dom";
import { useLastLocation } from "react-router-dom-last-location";
import { getInitiatives } from "../../api/initiatives";
import {
  getInvitationById,
  sendVendorCalendlySlots,
  submitAmbassadorResponse,
  submitAmbassadorSelectedSlot,
  submitInvitationRejected,
  submitMarkAsCancelled,
  submitMarkAsCompleted,
  submitVendorApproval,
  submitVendorSelectedSlot,
} from "../../api/invitations";
import { submitVendorAvailibility } from "../../api/projects";
import AppDrawer from "../../components/drawer";
import {
  AmbassadorDetailsButton,
  AppAlert,
  DefaultButton,
  DelegateSchedulingButton,
  LabelAndValue,
  LabelWithIcon,
} from "../../components/form";
import { EmptyDataImg } from "../../components/layouts";
import FlexBox from "../../components/layouts/Flexbox";
import AlertDialog from "../../components/modal/alert-dialog";
import { CONST_PROJECT_STATUS_LIST, GLOBAL_TEXT } from "../../constants";
import {
  APP_COLORS,
  ENUM_PROJECT_STATUS,
  ENUM_USER_ROLE,
  VIEW_MODE,
} from "../../enums";
import { useQueryWithDispatch } from "../../hooks/useQueryWithDispatch";
import useToggle from "../../hooks/useToggle";
import { concatSelectedFromToTime } from "../../services/utils/date-and-time.utils";
import {
  checkAndGetInvitationStatus,
  getDelegateEmails,
  getEffectiveProjectState,
  getProjectStatusConfig,
  invalidateMeetingsOverviewQueries,
  isCancelledOrRejected,
  shouldShowDelegateSchedulingButton,
} from "../my-projects/utils";
import { useAppDispatch, useAppSelector } from "../../store";
import { resetInvitationDetails, setInvitationDetailsData } from "../../store/modules/invitation";
import { showNotification } from "../../store/modules/UI";
import { createSelectedClientSlotsPayload } from "./utils";
import { ActiveProjectsSection } from "./ActiveProjectsSection";
import AmbassadorSlotSelection from "./ambassador-slot-selection";
import { StatusChip } from "./ProjectCard/project-card.styles";
import SlotHistorySection from "./SlotHistorySection";
import ProjectDetailsActionButtons from "./project-details-action-buttons";
import ProjectQuestions from "./project-questions";
import ProjectDetailsSkeleton from "./skeleton";
import VendorSlotSelectionModal from "./vendor-slot-selection";
import { AmbassadorSuggestedSlotAlert } from "./vendor-slot-selection/AmbassadorSuggestedSlotAlert";
import VendorSuggestSlots from "./vendor-slot-selection/VendorSuggestSlots";

export default function ProjectDetails({
  params: paramsProp,
  onCancel,
  projectDetails: projectDetailsProp,
  previewMode = false,
  onRefresh,
  action,
}: { params?: any; onCancel?: () => void; projectDetails?: any; previewMode?: boolean; onRefresh?: (data: any) => void, action?: string } = {}) {
  const isDrawerLayout = Boolean(onCancel);
  const routeParams = useParams();
  const params = paramsProp?.id ? paramsProp : routeParams;
  const { lastLocation } = useLastLocation();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { data: projectDetails, loading } = useAppSelector(
    (state) => state.invitation.details
  );
  const [idNotFound, setIdNotFound] = useState(false);
  const [ambassadorSelectedSlot, setAmbassadorSelectedSlot] = useState();
  const [selectedTimezone, setSelectedTimezone] = useState();
  const userData = useAppSelector((state) => state.auth.user);
  const [state, reducerDispatch] = useReducer(
    projectDetailsReducer,
    initialState
  );
  const [submitting, setSubmitting] = useState(false);
  const [showCancelConfirmAlert, setShowCancelConfirmAlert] = useToggle(false);
  const [showRejectConfirmAlert, setShowRejectConfirmAlert] = useToggle(false);
  const currentLocation = useLocation();
  const navigate = useNavigate();
  const [showSlotSelectionModal, setShowSlotSelectionModal] = useToggle(false);
  const [
    showAmbassadorSlotSelectionModal,
    setShowAmbassadorSlotSelectionModal,
  ] = useToggle(false);
  const [showVendorSuggestSlotsModal, setShowVendorSuggestSlotsModal] =
    useToggle(false);
  const [showSlotHistoryDrawer, setShowSlotHistoryDrawer] = useToggle(false);
  const autoOpenedActionRef = useRef<string | null>(null);
  const [isDefaultQuestionsRequired, setIsDefaultQuestionRequired] =
    useState(true);
  const [selectedInitiativeIds, setSelectedInitiativeIds] = useState<string[]>([]);
  const ambassadorSuggestedSlots = projectDetails?.suggestedTimeSlots;
  const { step } = getProjectStatusConfig(projectDetails) || {};
  const userRole = userData?.role;

  const isAmbassadorAnsweringQuestions =
    projectDetails?.status === ENUM_PROJECT_STATUS.REQUESTED &&
    userData?.role === ENUM_USER_ROLE.AMBASSADOR;

  const { data: initiativesResponse, isPending: initiativesLoading } =
    useQueryWithDispatch({
      queryKey: ["initiatives"],
      thunkAction: getInitiatives,
      variables: undefined,
      enabled: isAmbassadorAnsweringQuestions,
      showErrorToast: false,
    });
  const initiatives = initiativesResponse?.data ?? [];
  const privateInitiatives = initiatives.filter((i) => !i.isPublic);

  useEffect(() => {
    if (!action) {
      autoOpenedActionRef.current = null;
      return;
    }

    const actionKey = `${action}:${params?.id ?? ""}`;
    if (autoOpenedActionRef.current === actionKey) return;

    switch (action) {
      case "answer_questions":
        break;
      case "approve_or_reject_request":
        break;
      case "select_available_time": {
        if (!projectDetails) break;
        const invitationStatus = checkAndGetInvitationStatus(projectDetails);
        const canSelectAvailableTime =
          invitationStatus === ENUM_PROJECT_STATUS.VENDOR_RESCHEDULE_REQUESTED ||
          invitationStatus === ENUM_PROJECT_STATUS.APPROVED_BY_CLIENT;
        if (canSelectAvailableTime) {
          autoOpenedActionRef.current = actionKey;
          setShowSlotSelectionModal();
        }
        break;
      }
      case "provide_your_availability": {
        autoOpenedActionRef.current = actionKey;
        setShowAmbassadorSlotSelectionModal();
        break;
      }
      case "view":
        break;
      default:
        break;
    }
  }, [action, projectDetails, params?.id]);

  useEffect(() => {
    if (lastLocation) {
      if (
        !sessionStorage?.getItem("lastLocation") ||
        (typeof sessionStorage?.getItem("lastLocation") === "object" &&
          JSON.parse(sessionStorage?.getItem("lastLocation"))?.pathname !==
          currentLocation?.pathname)
      ) {
        sessionStorage.setItem("lastLocation", JSON.stringify(lastLocation));
      }
    }

    if (previewMode) {
      dispatch(setInvitationDetailsData(projectDetailsProp));
      onMount({ refreshList: false });
    } else {
      params?.id && onMount({ refreshList: false });
    }

    return () => {
      dispatch(resetInvitationDetails());
    };
  }, []);

  const refreshProjectsList = () => {
    invalidateMeetingsOverviewQueries(queryClient, userData?.role);
  };

  const onMount = async ({ refreshList = true } = {}) => {
    const response = previewMode ? { success: true, data: projectDetailsProp } : await dispatch(getInvitationById(params.id));

    if (previewMode) {
      dispatch(setInvitationDetailsData(projectDetailsProp));
    }

    if (response.success) {
      const data = response.data;
      const statusType = getEffectiveProjectState(data)?.effectiveCategory;

      refreshList && onRefresh?.(statusType);

      !data?.questions?.d1 && setIsDefaultQuestionRequired(false);

      reducerDispatch({
        type: "INITIAL_FORM_DATA",
        payload: {
          ...(data?.selectedTimeSlot && {
            selectedSlot: concatSelectedFromToTime(data?.selectedTimeSlot),
          }),
          d1: data?.questions?.d1?.a || "",
          d2: data?.questions?.d2?.a || "",
          q1: data?.questions?.q1?.a || "",
          q2: data?.questions?.q2?.a || "",
          q3: data?.questions?.q3?.a || "",
        },
      });

      if (userData?.isLoggedIn && statusType && routeParams?.id) {
        const redirectParams = new URLSearchParams({
          type: statusType,
          id: routeParams.id,
        });
        const invitationAction = new URLSearchParams(
          window.location.search
        ).get("action");
        const isVendorRole =
          userData?.role === ENUM_USER_ROLE.VENDOR ||
          userData?.role === ENUM_USER_ROLE.DELEGATE_VENDOR;
        const invitationStatus = checkAndGetInvitationStatus(data);

        if (
          userData?.role === ENUM_USER_ROLE.AMBASSADOR &&
          invitationStatus ===
            ENUM_PROJECT_STATUS.AMBASSADOR_RESCHEDULE_REQUESTED
        ) {
          redirectParams.set("action", "provide_your_availability");
        } else if (invitationAction) {
          redirectParams.set("action", invitationAction);
        } else if (
          isVendorRole &&
          invitationStatus === ENUM_PROJECT_STATUS.VENDOR_RESCHEDULE_REQUESTED
        ) {
          redirectParams.set("action", "reschedule");
        }

        navigate(`/project?${redirectParams.toString()}`, {
          replace: true,
        });
      }
    } else {
      setIdNotFound(true);
    }

    refreshList && refreshProjectsList();
  };

  const onSubmitSuccess = async (response) => {
    if (response?.success) {
      setSubmitting(false);
      await onMount();
      navigateBack();
    } else {
      onMount();
    }
  };

  const onSave = async () => {
    setSubmitting(true);
    const payload = {
      answers: {
        d1: state.d1,
        d2: state.d2,
        q1: state.q1,
        q2: state.q2,
        q3: state.q3,
      },
      ...(selectedInitiativeIds.length > 0 && { projectIds: selectedInitiativeIds }),
    };

    const response = await dispatch(
      submitAmbassadorResponse(payload, params?.id)
    );

    setSubmitting(false);
    onMount();
    navigateBack();
  };

  const onSendClick = async () => {
    setSubmitting(true);
    const response = await dispatch(submitVendorApproval(params?.id));
    setSubmitting(false);

    if (!response.success) {
      return false;
    } else {
      await onMount();
      setShowSlotSelectionModal();
    }
  };

  const onAddTimeslotClick = async () => {
    setShowSlotSelectionModal();
  };

  const onSendCalendlyInvite = async () => {
    setSubmitting(true);
    const response = await dispatch(sendVendorCalendlySlots(params?.id));

    if (response?.success) {
      await onMount();
      if (showSlotSelectionModal) {
        setShowSlotSelectionModal();
      }
      if (showVendorSuggestSlotsModal) {
        setShowVendorSuggestSlotsModal();
      }
      navigateBack();
    } else {
      onSubmitSuccess(response);
    }

    setSubmitting(false);
    return response;
  };

  const onMarkAsCompletedClick = async () => {
    setSubmitting(true);
    const response = await dispatch(submitMarkAsCompleted(params?.id));

    onSubmitSuccess(response);

    setSubmitting(false);
  };

  const onRejectClick = async () => {
    setShowRejectConfirmAlert();
  };

  const onRejectConfirm = async (message) => {
    setShowRejectConfirmAlert();
    setSubmitting(true);
    const response = await dispatch(
      submitInvitationRejected({ id: params?.id, message })
    );

    onSubmitSuccess(response);

    setSubmitting(false);
  };

  const onCancelledClick = async () => {
    setShowCancelConfirmAlert();
  };

  const onCancelConfirm = async (message) => {
    setShowCancelConfirmAlert();
    setSubmitting(true);
    const response = await dispatch(
      submitMarkAsCancelled({ id: params?.id, message })
    );

    onSubmitSuccess(response);

    setSubmitting(false);
  };

  const checkIfApprovable = () => {
    const tmpRequiredFields = {
      ...requiredFields,
    };

    if (!isDefaultQuestionsRequired) {
      delete tmpRequiredFields?.d1;
      delete tmpRequiredFields?.d2;
    }

    return Object.keys(tmpRequiredFields).some(
      (v) => !state[v] || !state[v].length
    );
  };

  const handleOnAnwerChange = (e) => {
    reducerDispatch({
      type: "SET_ANSWER_DATA",
      payload: { field: e.target.name, value: e.target.value },
    });
  };

  const onSelectTimeSlotClick = () => {
    setShowAmbassadorSlotSelectionModal();
  };

  const onAmbassadorTimeSlotSelect = (v, selectedTimezone) => {
    setAmbassadorSelectedSlot(v);
    setSelectedTimezone(selectedTimezone);
    setShowAmbassadorSlotSelectionModal();
  };

  const onAmbassadorSlotSubmit = async () => {
    setSubmitting(true);
    const payload = {
      selectedTimeSlot:
        projectDetails?.availableTimeSlots &&
          projectDetails?.availableTimeSlots?.length
          ? projectDetails?.availableTimeSlots.find(
            (f) => f.from === ambassadorSelectedSlot
          )
          : {
            from: moment(ambassadorSelectedSlot).toISOString(),
            to: moment(ambassadorSelectedSlot).add(1, "hour").toISOString(),
          },
      isCustom: false,
    };

    const response = await dispatch(
      submitAmbassadorSelectedSlot(payload, params?.id)
    );

    onSubmitSuccess(response);

    setSubmitting(false);
    onMount();
  };

  const onVendorSelectedSlot = async (payload) => {
    setSubmitting(true);
    const requestPayload = {
      selectedTimeSlot: payload.selectedTimeSlot || payload,
      isCustom: false,
      additionalMembers:
        payload.additionalMembers?.length > 0
          ? payload.additionalMembers?.map((member) => ({
            email: typeof member === "string" ? member : member.email,
          }))
          : [],
    };

    const response = await dispatch(
      submitVendorSelectedSlot(requestPayload, params?.id)
    );

    onSubmitSuccess(response);

    setSubmitting(false);
    onMount();
  };

  const onVendorSuggestSlotsClick = () => {
    setShowVendorSuggestSlotsModal();
  };

  const onVendorSuggestSlotsSubmit = async (
    slots: any[],
    additionalMembers?: Array<{ email: string }>
  ) => {
    setSubmitting(true);

    const slotsData = {
      slots: slots.map((slot) => ({
        selectedSlotTimeFrom: slot.selectedSlotTimeFrom,
        selectedSlotTimeTo: slot.selectedSlotTimeTo,
        isTimeRange: slot.isTimeRange || false,
        error: "",
      })),
    };

    const payload = createSelectedClientSlotsPayload(slotsData);

    const response: any = await dispatch(
      submitVendorAvailibility(
        {
          ...payload,
          availableTimeSlots: payload.availableTimeSlots.map((slot: any) => ({
            from: new Date(slot.from).toUTCString(),
            to: new Date(slot.to).toUTCString(),
          })),
          additionalMembers:
            additionalMembers?.length > 0
              ? additionalMembers?.map((member) => ({
                email: typeof member === "string" ? member : member.email,
              }))
              : [],
        },
        params?.id
      )
    );

    if (response.success) {
      setShowVendorSuggestSlotsModal();
      await onMount();
      navigateBack();
    } else {
      dispatch(
        showNotification({
          message:
            response.message ||
            "Invalid time selection. Please check all the time selections!",
          type: "error",
        })
      );
    }

    setSubmitting(false);
  };

  const renderProfileDetails = () => {
    const detailsOfRole = userData.role === ENUM_USER_ROLE.AMBASSADOR ? projectDetails?.client : projectDetails?.ambassador;

    return userData.role === ENUM_USER_ROLE.AMBASSADOR ? detailsOfRole?.company : detailsOfRole?.title;
  };

  const checkInvitationStatus = (project) => {
    const { effectiveStatus } = getEffectiveProjectState(project);
    if (
      !isCancelledOrRejected(project) &&
      CONST_PROJECT_STATUS_LIST[effectiveStatus]?.step >= 3
    ) {
      return false;
    }
    return true;
  };

  const navigateBack = () => {
    onCancel?.();
  };

  const delegateEmails = getDelegateEmails(projectDetails?.delegateData);

  if (idNotFound) return <EmptyDataImg />;

  if (loading) return <ProjectDetailsSkeleton drawerLayout={isDrawerLayout} />;

  return (
    <Stack
      height={isDrawerLayout ? "100%" : undefined}
      minHeight={isDrawerLayout ? 0 : undefined}
    >
      <Box
        sx={
          isDrawerLayout
            ? {
              overflowY: "auto",
              overflowX: "hidden",
              minHeight: 0,
              overscrollBehavior: "contain",
            }
            : undefined
        }
        flex={isDrawerLayout ? 1 : undefined}
        pb={2}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FlexBox
              width="100%"
              justifyContent="flex-end"
              bgcolor={"white"}
              alignItems="center"
              gap={1}
              flexWrap="wrap"
            >
              <StatusChip
                bgColor={
                  getProjectStatusConfig(projectDetails)?.projectStatusBgColor ||
                  APP_COLORS.successDark4
                }
                textColor={
                  getProjectStatusConfig(projectDetails)?.projectStatusTextColor ||
                  APP_COLORS.success
                }
                label={getProjectStatusConfig(projectDetails)?.label}
              />
              {ENUM_USER_ROLE.VENDOR === userRole &&
                shouldShowDelegateSchedulingButton(projectDetails) && (
                  <DelegateSchedulingButton
                    id={projectDetails?.id}
                    delegateData={projectDetails?.delegateData}
                  />
                )}
            </FlexBox>
          </Grid>

          {ambassadorSuggestedSlots?.length > 0 &&
            projectDetails?.status ===
            ENUM_PROJECT_STATUS.SUGGESTED_TIME_SLOT_BY_AMBASSADOR &&
            (ENUM_USER_ROLE.VENDOR === userData?.role ||
              ENUM_USER_ROLE.DELEGATE_VENDOR === userData?.role) && (
              <Grid item xs={12}>
                <AmbassadorSuggestedSlotAlert
                  slots={ambassadorSuggestedSlots}
                  onRejectClick={onCancelledClick}
                  onApproveClick={onVendorSelectedSlot}
                  onSubmitSuggestedSlots={onVendorSuggestSlotsClick}
                  submitting={submitting}
                  ambassadorDetails={projectDetails?.ambassador}
                  availableTimeSlots={projectDetails?.availableTimeSlots}
                  suggestedTimeSlots={projectDetails?.suggestedTimeSlots}
                  additionalMembers={projectDetails?.additionalMembers}
                />
              </Grid>
            )}

          <Grid item xs={12}>
            <Stack gap={0.5}>
              <Typography variant="bodyLarge-helveticaNeue-medium">
                {userData.role === ENUM_USER_ROLE.AMBASSADOR ? "Vendor" : "Ambassador"}
              </Typography>
              <Typography
                color={APP_COLORS.textSecondary}
                variant="bodyLarge-helveticaNeue"
              >
                {renderProfileDetails()}
              </Typography>
            </Stack>
          </Grid>

          {delegateEmails.length > 0 && (
            <Grid item xs={12}>
              <Stack gap={0.5}>
                <Typography variant="bodyLarge-helveticaNeue-medium">
                  Delegates
                </Typography>
                <FlexBox gap={1} flexWrap="wrap">
                  {delegateEmails.map((email) => (
                    <Chip key={email} label={email} size="small" />
                  ))}
                </FlexBox>
              </Stack>
            </Grid>
          )}

          {step > 3 && projectDetails?.selectedTimeSlot && (
            <Grid item xs={12}>
              <Card>
                <Stack gap={1.5}>
                  <Typography variant="heading6-helveticaNeue-medium">
                    Meeting time
                  </Typography>
                  <FlexBox>
                    <FlexBox alignItems="center" gap={2}>
                      <Typography variant="body-helveticaNeue-medium">
                        Selected Date
                      </Typography>
                      <Chip
                        label={moment(projectDetails?.selectedTimeSlot?.from).format("DD MMMM")}
                        color="info"
                        size="small"
                      />
                      <Typography variant="body-helveticaNeue-medium">
                        Selected Time
                      </Typography>
                      <Chip
                        label={concatSelectedFromToTime(
                          projectDetails?.selectedTimeSlot,
                          true
                        )}
                        color="warning"
                        size="small"
                      />
                    </FlexBox>
                  </FlexBox>
                </Stack>
              </Card>
            </Grid>
          )}

          {projectDetails?.status ===
            ENUM_PROJECT_STATUS.ADDED_TIME_SLOTS_BY_CLIENT &&
            userData.role === ENUM_USER_ROLE.AMBASSADOR && (
              <Grid item xs={12}>
                <Stack gap={1}>
                  <Typography variant="bodyLarge-helveticaNeue-medium">Select time</Typography>
                  <DefaultButton
                    onClick={onSelectTimeSlotClick}
                    fullWidth
                    startIcon={<Clock fontSize="small" />}
                  >
                    Please provide your availability
                  </DefaultButton>
                </Stack>
              </Grid>
            )}

          {(ENUM_USER_ROLE.VENDOR === userData.role) && (projectDetails?.status === ENUM_PROJECT_STATUS.RESPONDED) && (<Grid item xs={12}>
            <AmbassadorDetailsButton limitedAccess={checkInvitationStatus(projectDetails)} data={projectDetails?.ambassador} renderButton={
              <FlexBox bgcolor={APP_COLORS.secondaryLight} p={1} borderRadius={2} sx={{ cursor: "pointer" }}>
                <LabelWithIcon endIcon={<ArrowRightIcon size={20} />} icon={<NotepadIcon size={18} />} label="Explore Ambassador Initiatives"
                  labelProps={{ variant: "body-helveticaNeue", color: APP_COLORS.secondaryDark }}
                />
              </FlexBox>
            } />
          </Grid>)}

          <Grid item xs={12}>
            {projectDetails && (
              <ProjectQuestions
                mode={
                  projectDetails?.status === ENUM_PROJECT_STATUS.REQUESTED &&
                    userData.role === ENUM_USER_ROLE.AMBASSADOR
                    ? VIEW_MODE.EDIT
                    : VIEW_MODE.VIEW
                }
                questions={projectDetails?.questions}
                answers={state}
                handleOnAnwerChange={handleOnAnwerChange}
              />
            )}
          </Grid>

          {isAmbassadorAnsweringQuestions && privateInitiatives.length > 0 && (
            <Grid item xs={12}>
              <ActiveProjectsSection
                initiatives={privateInitiatives}
                loading={initiativesLoading}
                selectedInitiativeIds={selectedInitiativeIds}
                onSelectionChange={(init, selected) => {
                  setSelectedInitiativeIds((prev) =>
                    selected
                      ? [...prev, init.id]
                      : prev.filter((id) => id !== init.id)
                  );
                }}
              />
            </Grid>
          )}

          {projectDetails?.rejectMessage && (
            <Grid xs={12} item>
              <LabelAndValue
                label={"Reject/Canceled"}
                value={projectDetails?.rejectMessage}
                labelProps={{ variant: "heading6-helveticaNeue-medium" }}
                valueProps={{ variant: "body-helveticaNeue", color: APP_COLORS.errorDark }}
              />
            </Grid>
          )}

          <Grid item xs={12} mb={1}>
            {projectDetails?.status ===
              ENUM_PROJECT_STATUS.ADDED_TIME_SLOTS_BY_CLIENT &&
              userData.role === ENUM_USER_ROLE.VENDOR && (
                <Grid item xs={12} mb={1}>
                  <AppAlert severity="info">
                    {projectDetails?.availableTimeSlots &&
                      projectDetails?.availableTimeSlots?.length ? <>You have successfully suggested the following times for the
                        meeting with{" "}
                      <b>
                        {projectDetails?.ambassador?.title},{" "}
                        {projectDetails?.ambassador?.company}
                      </b>
                      . Once they select a time that works for them, you'll receive an
                      invitation</> : <>
                      You have successfully added the times via <b>Calendly</b> for the
                      meeting with{" "}
                      <b>
                        {projectDetails?.ambassador?.title},{" "}
                        {projectDetails?.ambassador?.company}
                      </b>
                      . Once they select a time that works for them, you'll receive an
                      invitation
                    </>}
                  </AppAlert>
                </Grid>
              )}
            {projectDetails?.timeSlotsHistory && (
              <Stack gap={1.5}>
                <MeetingRescheduleReasonSection
                  rescheduleReasonType={projectDetails?.rescheduleReasonType}
                  rescheduleReason={projectDetails?.rescheduleReason}
                />
                <TimesHistorySection
                  viewerRole={userData?.role}
                  onViewHistory={setShowSlotHistoryDrawer}
                />
              </Stack>
            )}
          </Grid>
          {showCancelConfirmAlert && (
            <AlertDialog
              message={GLOBAL_TEXT.ALERT_MESSAGES.CONFIRM_REJECT}
              onPrimaryActionClick={onCancelConfirm}
              userInputRequired
              handleClose={() => {
                setShowCancelConfirmAlert(false);
              }}
            />
          )}
          {showRejectConfirmAlert && (
            <AlertDialog
              message={GLOBAL_TEXT.ALERT_MESSAGES.CONFIRM_REJECT}
              onPrimaryActionClick={onRejectConfirm}
              userInputRequired
              handleClose={() => {
                setShowRejectConfirmAlert(false);
              }}
            />
          )}
          <AppDrawer
            open={showSlotSelectionModal}
            onClose={setShowSlotSelectionModal}
            title="Select your availability"
          >

            {ENUM_USER_ROLE.VENDOR === userRole &&
              shouldShowDelegateSchedulingButton(projectDetails) && (
                <FlexBox justifyContent="flex-end">
                  <DelegateSchedulingButton
                    id={projectDetails?.id}
                    delegateData={projectDetails?.delegateData}
                  />
                </FlexBox>
              )}
            <VendorSlotSelectionModal
              onCancel={setShowSlotSelectionModal}
              data={projectDetails}
              onAvailibilitySend={async () => {
                await onMount();
                setShowSlotSelectionModal();
                navigateBack();
              }}
              onSendCalendlyInvite={onSendCalendlyInvite}
            />
          </AppDrawer>
          {projectDetails && <AppDrawer
            title="Provide Availability"
            open={showAmbassadorSlotSelectionModal}
            onClose={setShowAmbassadorSlotSelectionModal}
          >
            <AmbassadorSlotSelection
              invitationId={params?.id}
              value={ambassadorSelectedSlot}
              timezone={selectedTimezone}
              onCancel={setShowAmbassadorSlotSelectionModal}
              projectDetails={projectDetails}
              availableDates={projectDetails?.availableTimeSlots?.map(
                (d) => d.from
              )}
              isCalendly={
                projectDetails?.availableTimeSlots &&
                  projectDetails?.availableTimeSlots?.length
                  ? false
                  : true
              }
              onSuccess={async () => {
                await onMount();
                setShowAmbassadorSlotSelectionModal();
                navigateBack();
              }}
            />
          </AppDrawer>}
          <AppDrawer
            title="Suggest times"
            open={showVendorSuggestSlotsModal}
            onClose={setShowVendorSuggestSlotsModal}
          >
            <VendorSuggestSlots
              invitationId={params?.id}
              projectDetails={projectDetails}
              onCancel={setShowVendorSuggestSlotsModal}
              onSubmit={onVendorSuggestSlotsSubmit}
              onSendCalendlyInvite={onSendCalendlyInvite}
            />
          </AppDrawer>
          <AppDrawer
            title="Times History"
            open={showSlotHistoryDrawer}
            onClose={setShowSlotHistoryDrawer}
          >
            <Box height="100vh" overflow="auto">
              <SlotHistorySection
                history={projectDetails?.timeSlotsHistory}
                viewerRole={userData?.role}
                showHeading={false}
              />
            </Box>
          </AppDrawer>
        </Grid>
      </Box>
      {!previewMode && isDrawerLayout && <Box
        position="sticky"
        bottom={0}
        zIndex={10}
        bgcolor="background.paper"
        borderTop={1}
        borderColor="divider"
        pt={2}
        mx={-2}
        px={2}
      >
        <ProjectDetailsActionButtons
          projectDetails={projectDetails}
          submitting={submitting}
          onSave={onSave}
          onCancel={navigateBack}
          onSendClick={onSendClick}
          onRejectClick={onRejectClick}
          onCancelledClick={onCancelledClick}
          onAmbassadorSlotSubmit={onAmbassadorSlotSubmit}
          ambassadorSelectedSlot={ambassadorSelectedSlot}
          checkIfApprovable={checkIfApprovable}
          onMarkAsCompletedClick={onMarkAsCompletedClick}
          onAddTimeslotClick={onAddTimeslotClick}
        />
      </Box>}
    </Stack>
  );
}

const initialState = {
  d1: "",
  d2: "",
  q1: "",
  q2: "",
  q3: "",
};

const requiredFields = {
  d1: "",
  d2: "",
  q1: "",
  q2: "",
  q3: "",
};

function projectDetailsReducer(state, action) {
  switch (action.type) {
    case "INITIAL_FORM_DATA":
      return {
        ...state,
        ...action.payload,
      };
    case "SET_ANSWER_DATA":
      return {
        ...state,
        [action.payload.field]: action.payload.value,
      };

    default:
      return { ...state };
  }
}
